import type {
  FormActivityUI,
  FormBlockUI,
  FormLessonUI,
} from "../../../components/Forms/LessonForm/utils";
import type { LessonFormContextValue } from "../../../components/Forms/LessonForm/components/LessonFormContext";
import type { OdReviewIssue, OdReviewTargetField } from "../../../services/odReview/odReview.types";

export interface ResolvedIndices {
  activityIndex: number;
  blockIndex: number;
}

export function resolveIndices(
  lesson: FormLessonUI,
  issue: OdReviewIssue,
): ResolvedIndices {
  const activityIndex = lesson.activities.findIndex(
    (a: FormActivityUI) => a.id === issue.activity_id,
  );
  if (activityIndex < 0) return { activityIndex: -1, blockIndex: -1 };
  if (issue.block_id === null) return { activityIndex, blockIndex: -1 };
  const blockIndex = lesson.activities[activityIndex].blocks.findIndex(
    (b: FormBlockUI) => b.id === issue.block_id,
  );
  return { activityIndex, blockIndex };
}

export function readCurrentValue(
  lesson: FormLessonUI,
  issue: OdReviewIssue,
  activityIndex: number,
  blockIndex: number,
): string | null {
  if (activityIndex < 0) return null;
  const activity = lesson.activities[activityIndex];
  switch (issue.target_field) {
    case "title":
      return activity.title;
    case "description":
      return activity.summary ?? null;
    case "instructions":
      if (blockIndex < 0) return null;
      return activity.blocks[blockIndex]?.text ?? null;
    case "materials":
      if (blockIndex < 0) return null;
      return activity.blocks[blockIndex]?.materials ?? null;
    case "photo":
    case "resource":
    case "resource_reference":
      return null;
    default:
      return null;
  }
}

export type OdReviewFieldSetters = Pick<
  LessonFormContextValue,
  "handleActivityUpdate" | "handleBlockTextChange" | "handleBlockMaterialsChange"
>;

type FieldWriter = (
  setters: OdReviewFieldSetters,
  activityIndex: number,
  blockIndex: number,
  value: string,
) => void;

const FIELD_WRITERS: Partial<Record<OdReviewTargetField, FieldWriter>> = {
  title: (setters, activityIndex, _blockIndex, value) =>
    setters.handleActivityUpdate(activityIndex, "title", value),
  description: (setters, activityIndex, _blockIndex, value) =>
    setters.handleActivityUpdate(activityIndex, "summary", value),
  instructions: (setters, activityIndex, blockIndex, value) =>
    setters.handleBlockTextChange(activityIndex, blockIndex, value),
  materials: (setters, activityIndex, blockIndex, value) =>
    setters.handleBlockMaterialsChange(activityIndex, blockIndex, value),
};

export function writeValue(
  setters: OdReviewFieldSetters,
  issue: OdReviewIssue,
  activityIndex: number,
  blockIndex: number,
  value: string,
): void {
  if (activityIndex < 0) return;
  const writer = FIELD_WRITERS[issue.target_field];
  if (!writer) return;
  writer(setters, activityIndex, blockIndex, value);
}

function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  let count = 0;
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    count += 1;
    index = haystack.indexOf(needle, index + needle.length);
  }
  return count;
}

export function applyPatchClientSide(
  current: string | null,
  issue: OdReviewIssue,
): string | null {
  if (current === null) return null;
  if (issue.operation === "manual") return null;
  if (issue.operation === "add") {
    if (!issue.suggested_value) return null;
    return current ? `${current} ${issue.suggested_value}` : issue.suggested_value;
  }
  if (!issue.original_value) return null;
  if (countOccurrences(current, issue.original_value) !== 1) return null;
  if (issue.operation === "remove") {
    return current.replace(issue.original_value, "");
  }
  if (issue.operation === "replace") {
    if (issue.suggested_value === null) return null;
    return current.replace(issue.original_value, issue.suggested_value);
  }
  return null;
}
